import type { CSSProperties, ReactNode } from 'react';
import { Badge, Button, Icon, type IconName } from '@/components/primitives';

interface ConfigCategoryCardProps {
  icon: IconName;
  title: string;
  count: number;
  countLabel?: string;
  why: string;
  example: string;
  cta: string;
  onClick: () => void;
  tone?: 'primary' | 'warning' | 'success';
  children?: ReactNode;
  style?: CSSProperties;
}

const TONE_BG: Record<NonNullable<ConfigCategoryCardProps['tone']>, string> = {
  primary: '#eef2ff',
  warning: '#fff7ed',
  success: '#ecfdf5',
};
const TONE_FG: Record<NonNullable<ConfigCategoryCardProps['tone']>, string> = {
  primary: 'var(--bx-primary)',
  warning: '#c2410c',
  success: '#047857',
};

export function ConfigCategoryCard({
  icon,
  title,
  count,
  countLabel,
  why,
  example,
  cta,
  onClick,
  tone = 'primary',
  children,
  style,
}: ConfigCategoryCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        background: 'var(--bx-card-bg)',
        border: '1px solid var(--bx-border-muted)',
        borderRadius: 'var(--bx-radius-lg)',
        boxShadow: 'var(--bx-shadow-card)',
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        cursor: 'pointer',
        transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        minWidth: 0,
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = TONE_FG[tone];
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--bx-border-muted)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: TONE_BG[tone],
            color: TONE_FG[tone],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={icon} size={18} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--bx-text-heading)',
                lineHeight: 1.3,
              }}
            >
              {title}
            </div>
            <Badge kind="INFO" size="sm">
              {count} {countLabel ?? 'mục'}
            </Badge>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--bx-text-muted)', lineHeight: 1.5 }}>
            {why}
          </div>
        </div>
      </div>

      {children && (
        <div
          style={{
            padding: 10,
            background: 'var(--bx-surface-2)',
            borderRadius: 8,
            minHeight: 48,
          }}
        >
          {children}
        </div>
      )}

      <div
        style={{
          fontSize: 12,
          color: 'var(--bx-text-body)',
          lineHeight: 1.5,
          padding: '8px 10px',
          background: TONE_BG[tone],
          borderRadius: 8,
          borderLeft: `3px solid ${TONE_FG[tone]}`,
        }}
      >
        <span style={{ fontWeight: 600, color: TONE_FG[tone] }}>Ví dụ: </span>
        {example}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="ghost-primary" size="sm" icon="arrowRight">
          {cta}
        </Button>
      </div>
    </div>
  );
}
